import apiClient from '../client'

// Types
export interface AgentProfile {
  id: number
  agent_key: string
  pool_id: number | null
  name: string
  email: string | null
  gender: string
  title: string
  company: string
  voice_id: string | null
  voice_name: string | null
  voice_provider: string
  primary_language: string
  primary_language_name: string | null
  supported_languages: string[] | null
  language_proficiency: Record<string, string> | null
  is_primary: boolean
  is_outbound_default: boolean
  display_order: number
  is_active: boolean
  assigned_phone_number: string | null
  transfer_phone_number: string | null
  transfer_greeting: string | null
  custom_greeting: string | null
  custom_personality_traits: Record<string, unknown> | null
  total_calls: number
  successful_transfers_received: number
  successful_transfers_sent: number
  average_call_duration: number
  created_at: string | null
  updated_at: string | null
}

export interface AgentPool {
  id: number
  name: string
  description: string | null
  language_detection_enabled: boolean
  auto_transfer_enabled: boolean
  is_active: boolean
  created_by: string | null
  created_at: string | null
  updated_at: string | null
  agents: AgentProfile[]
}

export interface AgentPoolListResponse {
  pools: AgentPool[]
  total: number
}

export interface AgentProfileListResponse {
  agents: AgentProfile[]
  total: number
}

export interface TransferStatsResponse {
  total_transfers: number
  successful_transfers: number
  failed_transfers: number
  by_language: Record<string, number>
}

export interface CreateAgentProfileRequest {
  agent_key: string
  pool_id?: number | null
  name: string
  email?: string | null
  gender?: string
  title?: string
  company?: string
  voice_id?: string | null
  voice_name?: string | null
  voice_provider?: string
  primary_language: string
  primary_language_name?: string | null
  supported_languages?: string[] | null
  language_proficiency?: Record<string, string> | null
  is_primary?: boolean
  is_outbound_default?: boolean
  display_order?: number
  is_active?: boolean
  assigned_phone_number?: string | null
  transfer_phone_number?: string | null
  transfer_greeting?: string | null
  custom_greeting?: string | null
  custom_personality_traits?: Record<string, unknown> | null
}

export interface UpdateAgentProfileRequest {
  name?: string
  email?: string | null
  gender?: string
  title?: string
  company?: string
  voice_id?: string | null
  voice_name?: string | null
  voice_provider?: string
  primary_language?: string
  primary_language_name?: string | null
  supported_languages?: string[] | null
  language_proficiency?: Record<string, string> | null
  is_primary?: boolean
  is_outbound_default?: boolean
  display_order?: number
  is_active?: boolean
  assigned_phone_number?: string | null
  transfer_phone_number?: string | null
  transfer_greeting?: string | null
  custom_greeting?: string | null
  custom_personality_traits?: Record<string, unknown> | null
}

export interface CreateAgentPoolRequest {
  name: string
  description?: string | null
  language_detection_enabled?: boolean
  auto_transfer_enabled?: boolean
  is_active?: boolean
}

export interface UpdateAgentPoolRequest {
  name?: string
  description?: string | null
  language_detection_enabled?: boolean
  auto_transfer_enabled?: boolean
  is_active?: boolean
}

export interface ConfigureAgentNumberRequest {
  phone_number: string
  configure_bland?: boolean
}

export interface ConfigureAgentNumberResponse {
  success: boolean
  agent_key: string
  agent_name: string | null
  phone_number: string | null
  bland_configured: boolean
  message: string
  error: string | null
}

// API client
export const agentPoolApi = {
  // Pool endpoints
  getPools: (includeAgents: boolean = true) =>
    apiClient.get<AgentPoolListResponse>('/api/agent-pool/pools', {
      params: { include_agents: includeAgents }
    }),

  getPool: (poolId: number) =>
    apiClient.get<AgentPool>(`/api/agent-pool/pools/${poolId}`),

  createPool: (data: CreateAgentPoolRequest) =>
    apiClient.post<AgentPool>('/api/agent-pool/pools', data),

  updatePool: (poolId: number, data: UpdateAgentPoolRequest) =>
    apiClient.put<AgentPool>(`/api/agent-pool/pools/${poolId}`, data),

  // Agent endpoints
  getAgents: (params?: { pool_id?: number; is_active?: boolean; language?: string }) =>
    apiClient.get<AgentProfileListResponse>('/api/agent-pool/agents', { params }),

  getAgent: (agentId: number) =>
    apiClient.get<AgentProfile>(`/api/agent-pool/agents/${agentId}`),

  createAgent: (data: CreateAgentProfileRequest) =>
    apiClient.post<AgentProfile>('/api/agent-pool/agents', data),

  updateAgent: (agentId: number, data: UpdateAgentProfileRequest) =>
    apiClient.put<AgentProfile>(`/api/agent-pool/agents/${agentId}`, data),

  deleteAgent: (agentId: number) =>
    apiClient.delete<{ success: boolean; message: string }>(`/api/agent-pool/agents/${agentId}`),

  // Stats endpoints
  getTransferStats: () =>
    apiClient.get<TransferStatsResponse>('/api/agent-pool/stats/transfers'),

  // Phone number configuration
  configureAgentNumber: (agentKey: string, data: ConfigureAgentNumberRequest) =>
    apiClient.post<ConfigureAgentNumberResponse>(`/api/agent-pool/agents/${agentKey}/configure-number`, data),

  removeAgentNumber: (agentKey: string) =>
    apiClient.delete<{ success: boolean; message: string }>(`/api/agent-pool/agents/${agentKey}/phone-number`),
}
